import { UseQueryResult, useQuery } from 'react-query'

import { Product } from 'components/product-card'

import { ProductCategory } from 'hooks/use-product-categories'

import httpClient from 'services/http-client'

export const GET_PRODUCTS = '/products/search'

export type NumericRange = {
  from?: number | undefined
  to?: number | undefined
}

export type ProductResult = {
  results: Product[]
  total: number
  priceRange: NumericRange[]
  madeInCountries: string[]
  count: number
  categories: ProductCategory[]
  offset: number
}

export type CommerceToolsSearchQuery = {
  priceRange?: string[]
  fullTextSearch?: string
  promotion?: string[]
  brandLocation?: string[]
  brandName?: string[]
  brandKey?: string[]
  categories?: string[]
  madeIn?: string[]
  bestSeller?: boolean
  offset: number
  limit: number
  fuzzy?: boolean
  fuzzyLevel?: number
  primaryProductId?: string
  firstPublishedDateUnix?: NumericRange[]
  filterFacets?: {
    brandKey?: string[]
  }
}

export const ALL_IN_ONE_PAGE_WITHOUT_LIMIT = -1
const MAX_FETCH_LIMIT = 5 // prevent fetching too many pages
const MAX_PAGE_LIMIT = 500

const getFuzzyLevel = (searchText: string) => {
  const textLength = searchText.length
  switch (textLength) {
    case 1:
    case 2:
      return 0
    case 3:
    case 4:
    case 5:
      return 1
    default:
      return 2
  }
}

export const useProductSearch = (query: CommerceToolsSearchQuery): UseQueryResult<ProductResult> => {
  const isFetchAllPage = query.limit === ALL_IN_ONE_PAGE_WITHOUT_LIMIT

  const _query: CommerceToolsSearchQuery = {
    ...query,
    limit: isFetchAllPage ? MAX_PAGE_LIMIT : query.limit,
    fullTextSearch: encodeURI(query.fullTextSearch ?? ''),
  }

  return useQuery({
    queryKey: [GET_PRODUCTS, _query, isFetchAllPage, MAX_FETCH_LIMIT],
    queryFn: async () => {
      let allProducts: ProductResult = {
        results: [],
        total: 0,
        priceRange: [],
        madeInCountries: [],
        count: 0,
        categories: [],
        offset: 0,
      }
      let hasNextPage = true
      let fetchCallCount = 0

      do {
        const { data: productsRes } = await httpClient.get<ProductResult>(GET_PRODUCTS, {
          headers: {
            'x-products-search': JSON.stringify({ ..._query, offset: allProducts.offset }),
          },
        })

        allProducts = {
          ...productsRes,
          count: productsRes.total,
          results: [...allProducts.results, ...productsRes.results],
        }

        // fetch only one page
        if (!isFetchAllPage) {
          break
        }

        hasNextPage = productsRes.total ? allProducts.results.length < productsRes.total : false
        fetchCallCount++

        if (hasNextPage) {
          allProducts.offset += _query.limit
        }
      } while (hasNextPage && fetchCallCount <= MAX_FETCH_LIMIT)

      if (allProducts.count === 0) {
        const { data: fuzzyProducts } = await httpClient.get<ProductResult>(GET_PRODUCTS, {
          headers: {
            'x-products-search': JSON.stringify({
              ..._query,
              fuzzy: true,
              fuzzyLevel: getFuzzyLevel(_query.fullTextSearch ?? ''),
            }),
          },
        })
        return fuzzyProducts
      }

      return allProducts
    },
  })
}
